import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, Loader2 } from 'lucide-react';

const SearchBar = ({
  value: controlledValue,
  onChange,
  onSearch,
  placeholder = 'Search...',
  debounceMs = 300,
  expandable = false,
  showShortcut = false,
  loading = false,
  size = 'md',
  className = '',
  containerClassName = '',
}) => {
  const [internalValue, setInternalValue] = useState('');
  const [expanded, setExpanded] = useState(!expandable);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const val = controlledValue !== undefined ? controlledValue : internalValue;

  const handleChange = useCallback(
    (e) => {
      const v = e.target.value;
      if (controlledValue === undefined) setInternalValue(v);
      onChange?.(v);

      if (onSearch) {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => onSearch(v), debounceMs);
      }
    },
    [controlledValue, onChange, onSearch, debounceMs],
  );

  const handleClear = () => {
    if (controlledValue === undefined) setInternalValue('');
    onChange?.('');
    onSearch?.('');
    inputRef.current?.focus();
  };

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    if (!showShortcut) return;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (expandable) setExpanded(true);
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [expandable, showShortcut]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const sizeStyles = {
    sm: { input: 'py-1.5 text-[11px]', icon: 12, container: 'h-7' },
    md: { input: 'py-2 text-[12.5px]', icon: 13, container: 'h-8' },
    lg: { input: 'py-2.5 text-[13px]', icon: 14, container: 'h-9' },
  };
  const s = sizeStyles[size];

  if (expandable && !expanded) {
    return (
      <button
        onClick={() => { setExpanded(true); requestAnimationFrame(() => inputRef.current?.focus()); }}
        className={`p-2 rounded-[10px] text-surface-500 hover:text-surface-600 hover:bg-surface-100/70 transition-all ${containerClassName}`}
      >
        <Search size={s.icon} />
      </button>
    );
  }

  return (
    <div className={`relative ${containerClassName}`}>
      <div className="relative group">
        {loading ? (
          <Loader2 size={s.icon} className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-900 animate-spin" />
        ) : (
          <Search
            size={s.icon}
            className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-200 ${
              focused ? 'text-primary-900' : 'text-surface-500'
            }`}
          />
        )}
        <input
          ref={inputRef}
          type="text"
          value={val}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); if (expandable && !val) setExpanded(false); }}
          onKeyDown={(e) => e.key === 'Escape' && inputRef.current?.blur()}
          placeholder={placeholder}
          className={`
            w-full pl-9 pr-${val ? '8' : showShortcut ? '14' : '3.5'} ${s.input}
            bg-surface-50/60 border-[1.5px] border-surface-200/60
            text-surface-800 placeholder-surface-400
            transition-all duration-250
            hover:border-surface-300
            focus:border-primary-900 focus:ring-[3px] focus:ring-primary-900/8 focus:bg-white
            ${className}
          `}
          style={{ borderRadius: 'var(--radius-input)' }}
        />

        {/* Clear btn */}
        {val && (
          <button
            onClick={handleClear}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-surface-500 hover:text-surface-600 rounded-[6px] hover:bg-surface-100 transition-all"
          >
            <X size={12} />
          </button>
        )}

        {/* Shortcut hint */}
        {showShortcut && !val && !focused && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-px">
            <kbd className="text-[11px] font-semibold text-surface-500 bg-surface-100 px-1.5 py-0.5 rounded-[5px] border border-surface-200/50">
              ⌘K
            </kbd>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
